import { api, ApiError, getChatId } from "./api";
import { renderMarkdown } from "./md";
import { t } from "./i18n";

interface Msg { role: "user" | "assistant"; text: string; }
interface ToolState {
  id: string;
  name: string;
  arguments?: unknown;
  result?: unknown;
  status: "running" | "done";
}

export async function renderChat(el: HTMLElement): Promise<void> {
  const chatId = getChatId();
  el.innerHTML = `
    <div class="chat-head">
      <div id="access-state" class="access-state" hidden></div>
      <button id="clear-chat" class="icon-btn" type="button" aria-label="${t("chat.clear")}" title="${t("chat.clear")}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v5"/><path d="M14 11v5"/>
        </svg>
      </button>
    </div>
    <div class="chat-log" id="log"></div>
    <form class="chat-input" id="f">
      <input id="t" placeholder="${t("chat.placeholder")}" autocomplete="off" inputmode="text" />
      <button type="submit" class="btn icon-only" aria-label="${t("chat.send")}">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
      </button>
    </form>
  `;
  el.classList.add("chat-mode");

  const log = el.querySelector<HTMLDivElement>("#log")!;
  const form = el.querySelector<HTMLFormElement>("#f")!;
  const input = el.querySelector<HTMLInputElement>("#t")!;
  const accessState = el.querySelector<HTMLDivElement>("#access-state")!;
  const clearButton = el.querySelector<HTMLButtonElement>("#clear-chat")!;

  const push = (message: Msg): void => {
    const div = document.createElement("div");
    div.className = `msg ${message.role}`;
    if (message.role === "assistant") div.innerHTML = renderMarkdown(message.text);
    else div.textContent = message.text;
    log.appendChild(div);
    scrollToBottom(log);
  };

  try {
    const history = await api.chatHistory(chatId);
    for (const message of history.messages) {
      if (message.role === "user" && message.content) {
        push({ role: "user", text: String(message.content) });
      } else if (message.role === "assistant" && String(message.content || "").trim()) {
        push({ role: "assistant", text: String(message.content) });
      }
    }
  } catch {
    // A fresh chat has no history.
  }

  let accessAllowed = await refreshAccess(accessState, form);
  const onVisible = async () => {
    if (document.visibilityState !== "visible") return;
    accessAllowed = await refreshAccess(accessState, form);
  };
  document.addEventListener("visibilitychange", onVisible);

  clearButton.addEventListener("click", async () => {
    if (!confirm(t("chat.clear_confirm"))) return;
    clearButton.disabled = true;
    try {
      await api.clearChatHistory(chatId);
      log.replaceChildren();
    } catch (error: any) {
      push({ role: "assistant", text: t("chat.clear_error", { msg: error.message }) });
    } finally {
      clearButton.disabled = false;
    }
  });

  form.addEventListener("submit", async event => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text || !accessAllowed) return;
    input.value = "";
    push({ role: "user", text });

    const view = createAssistantView(log, chatId);
    setFormBusy(form, true);
    try {
      await api.streamChatMessage(text, chatId, event => view.handle(event));
      view.finish();
    } catch (error: any) {
      const apiError = error as ApiError;
      if (apiError.code === "BOT_AUTH_REQUIRED" || apiError.code === "SUBSCRIPTION_REQUIRED") {
        accessAllowed = await refreshAccess(accessState, form);
        view.removeIfEmpty();
      } else {
        view.fail(error.message || t("chat.temp_error"));
      }
    } finally {
      setFormBusy(form, false);
      if (!accessAllowed) form.hidden = true;
      input.focus();
    }
  });

  window.addEventListener("hashchange", () => {
    document.removeEventListener("visibilitychange", onVisible);
  }, { once: true });
}

function createAssistantView(log: HTMLDivElement, chatId: string) {
  const root = document.createElement("div");
  root.className = "msg assistant";
  log.appendChild(root);

  let text = "";
  let progress = t("chat.thinking");
  let failed = false;
  const tools = new Map<string, ToolState>();
  let toolSequence = 0;
  let choices: {
    questionId: string;
    options: string[];
    multi: boolean;
    selected: Set<number>;
  } | null = null;
  const photos: { url: string; caption: string }[] = [];

  const render = () => {
    const body = text ? renderMarkdown(text) : `<p class="stream-status">${esc(progress)}</p>`;
    const toolTrace = tools.size ? `
      <div class="tool-trace">
        ${[...tools.values()].map(tool => `
          <details class="tool-event" ${tool.status === "running" ? "open" : ""}>
            <summary>
              <span class="tool-state ${tool.status}"></span>
              <code>${esc(tool.name)}</code>
              <span>${tool.status === "running" ? t("chat.tool_running") : t("chat.tool_done")}</span>
            </summary>
            ${tool.arguments !== undefined ? `<pre>${esc(pretty(tool.arguments))}</pre>` : ""}
            ${tool.result !== undefined ? `<pre>${esc(pretty(tool.result))}</pre>` : ""}
          </details>
        `).join("")}
      </div>
    ` : "";
    const photoList = photos.map(photo => `
      <figure class="chat-photo">
        <img src="${escAttr(photo.url)}" alt="" loading="lazy" />
        ${photo.caption ? `<figcaption>${esc(photo.caption)}</figcaption>` : ""}
      </figure>
    `).join("");
    const choiceBlock = choices ? `
      <div class="choice-block" data-question-id="${escAttr(choices.questionId)}">
        ${choices.options.map((option, index) => `
          <button
            type="button"
            class="choice-btn ${choices?.selected.has(index) ? "selected" : ""}"
            aria-pressed="${choices?.selected.has(index) ? "true" : "false"}"
            data-choice-index="${index}"
          >${esc(option)}</button>
        `).join("")}
        ${choices.multi ? `
          <button
            type="button"
            class="choice-submit"
            data-choice-submit
            ${choices.selected.size ? "" : "disabled"}
          >${t("chat.choice_submit")}</button>
        ` : ""}
      </div>
    ` : "";
    root.innerHTML = body + toolTrace + photoList + choiceBlock;
    bindChoices();
    scrollToBottom(log);
  };

  const submitChoice = async (selected: number[]) => {
    if (!choices || !selected.length) return;
    const questionId = choices.questionId;
    choices = null;
    progress = t("chat.continuing");
    render();
    try {
      await api.answerChoice(questionId, selected);
      await resumeEvents(chatId, event => handle(event));
    } catch (error: any) {
      fail(error.message || t("chat.choice_error"));
    }
  };

  const bindChoices = () => {
    if (!choices) return;
    root.querySelectorAll<HTMLButtonElement>("[data-choice-index]").forEach(button => {
      button.addEventListener("click", () => {
        if (!choices) return;
        const index = Number(button.dataset.choiceIndex);
        if (!choices.multi) {
          void submitChoice([index]);
          return;
        }
        if (choices.selected.has(index)) choices.selected.delete(index);
        else choices.selected.add(index);
        render();
      }, { once: true });
    });
    root.querySelector<HTMLButtonElement>("[data-choice-submit]")?.addEventListener(
      "click",
      () => {
        if (choices) void submitChoice([...choices.selected].sort((a, b) => a - b));
      },
      { once: true },
    );
  };
  const handle = (event: any): void => {
    if (event.type === "say_delta" && event.text) {
      text += String(event.text);
      progress = "";
    } else if (event.type === "say" && event.text !== undefined) {
      text = String(event.text);
      progress = "";
    } else if (event.type === "progress" && event.text) {
      progress = String(event.text);
    } else if (event.type === "progress_done") {
      progress = "";
    } else if (event.type === "tool_call") {
      const id = String(event.id || `tool-${++toolSequence}`);
      tools.set(id, {
        id,
        name: String(event.name || "tool"),
        arguments: event.arguments,
        status: "running",
      });
    } else if (event.type === "tool_result") {
      const id = event.id ? String(event.id) : findRunningTool(tools);
      const current = id ? tools.get(id) : undefined;
      if (current) {
        current.status = "done";
        current.result = event.content ?? event.result;
      } else {
        const fallbackId = `tool-${++toolSequence}`;
        tools.set(fallbackId, {
          id: fallbackId,
          name: String(event.name || "tool"),
          result: event.content ?? event.result,
          status: "done",
        });
      }
    } else if (event.type === "ask_choice" && event.question_id && Array.isArray(event.options)) {
      choices = {
        questionId: String(event.question_id),
        options: event.options.map(String),
        multi: Boolean(event.multi_select),
        selected: new Set<number>(),
      };
      if (event.text) progress = String(event.text);
    } else if (event.type === "photo" && event.url) {
      photos.push({ url: String(event.url), caption: String(event.caption || event.text || "") });
    } else if (event.type === "error") {
      fail(String(event.error || event.text || t("chat.temp_error")));
      return;
    } else if (event.type === "done" && event.reply_text !== undefined) {
      text = String(event.reply_text || text);
      progress = "";
    }
    render();
  };

  const finish = () => {
    if (!text && !failed && !choices) progress = t("chat.empty_reply");
    render();
  };

  const fail = (message: string) => {
    failed = true;
    progress = `Ошибка: ${message}`;
    root.classList.add("error");
    render();
  };

  render();
  return {
    handle,
    finish,
    fail,
    removeIfEmpty: () => {
      if (!text && !tools.size) root.remove();
    },
  };
}

async function resumeEvents(chatId: string, onEvent: (event: any) => void): Promise<void> {
  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    const response = await api.chatEvents(chatId, 25);
    for (const event of response.messages) {
      onEvent(event);
      if (event.type === "say" || event.type === "done" || event.type === "error") return;
    }
  }
  throw new Error(t("chat.resume_timeout"));
}

async function refreshAccess(
  target: HTMLDivElement,
  form: HTMLFormElement,
): Promise<boolean> {
  try {
    await api.chatAccess();
    target.hidden = true;
    target.replaceChildren();
    form.hidden = false;
    return true;
  } catch (error: any) {
    if (error.code !== "BOT_AUTH_REQUIRED" && error.code !== "SUBSCRIPTION_REQUIRED") {
      target.hidden = false;
      target.textContent = t("chat.api_unavailable", { msg: error.message });
      form.hidden = true;
      return false;
    }
    const config = await api.chatConfig();
    const needsAuth = error.code === "BOT_AUTH_REQUIRED";
    const title = needsAuth ? t("chat.auth_title") : t("chat.sub_title");
    const text = needsAuth ? t("chat.auth_text") : t("chat.sub_text");
    const link = needsAuth ? config.bot_auth_link : config.buy_link;
    target.hidden = false;
    target.innerHTML = `
      <strong>${esc(title)}</strong>
      <span>${esc(text)}</span>
      <a class="btn" href="${escAttr(link)}" target="_blank" rel="noopener">${t("chat.open_telegram")}</a>
    `;
    form.hidden = true;
    return false;
  }
}

function setFormBusy(form: HTMLFormElement, busy: boolean): void {
  form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input,button").forEach(control => {
    control.disabled = busy;
  });
}

function findRunningTool(tools: Map<string, ToolState>): string | undefined {
  return [...tools.values()].reverse().find(tool => tool.status === "running")?.id;
}

function pretty(value: unknown): string {
  const result = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return String(result || "").slice(0, 4000);
}

function scrollToBottom(log: HTMLDivElement): void {
  log.scrollTop = log.scrollHeight;
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, char => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]!
  ));
}

function escAttr(value: unknown): string {
  return esc(value).replace(/`/g, "&#96;");
}
